import { Component, inject } from "@angular/core";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { finalize, map, take, takeUntil } from "rxjs/operators";
import { JobDetail } from "../../../../models/jobDetail";
import { CachedUserService } from "../../../../shared/services/cached-user.service";
import { firstValueFrom, forkJoin, Subject } from "rxjs";
import { SharedModule } from "../../../../shared/shared.module";
import {
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  Validators,
} from "@angular/forms";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { Job } from "../../../../models/job";
import { Project } from "../../../../models/project";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { Router } from "@angular/router";
import { ProjectService } from "../../../../shared/services/project.service";
import { JobType, JOB_TYPES } from "../../../../models/jobType";

interface EditJobModalData {
  jobId: string;
}

@Component({
    selector: "app-edit-job",
    templateUrl: "./edit-job-dialog.component.html",
    styleUrls: ["./edit-job-dialog.component.less"],
    imports: [SharedModule]
})
export class EditJobDialogComponent {
  private destroy$ = new Subject<void>();
  readonly #modal = inject(NzModalRef);
  readonly nzModalData: EditJobModalData = inject(NZ_MODAL_DATA);
  cachedProjectService = inject(CachedProjectService);
  projectService = inject(ProjectService)
  cachedUserService = inject(CachedUserService);
  notificationService = inject(NzNotificationService)
  _fb = inject(FormBuilder);
  router = inject(Router)

  currentJob: JobDetail;
  loading: boolean;
  allProjects: {
    id: string;
    name: string;
    description: string;
    field?: string;
  }[];
  allUsers: { email: string; id: string }[];
  jobTypes: JobType[];
  jobForm: FormGroup;
  allJobs: Job[] = [];
  parentJobMaxIters: number;
  project: Project; // keeping track of currentProject, for navigation later on

  constructor() {
    this.jobForm = this._fb.group(
      {
        name: [null, Validators.required],
        projectId: [null, Validators.required],
        basePath: [null, Validators.required],
        iterations: [
          null,
          [
            Validators.required,
            Validators.pattern("^[0-9]+$"),
            this.iterationsValidator,
          ],
        ],
        iterationsComplete: [
          null,
          [
            Validators.required,
            Validators.pattern("^[0-9]+$"),
            this.iterationsValidator,
          ],
        ],
        comments: [null],
        owner: [null, Validators.required],
        parentJob: [null],
        parentCpNum: [null], // validators added later on for this field (handleParentJobChange)
        isOffshoot: [false],
        isAWS: [false],
        jobType: [null, Validators.required],
        originalPath: [null]
      },
      {},
    );
  }

  ngOnInit() {
    this.loading = true;
    this.jobTypes = JOB_TYPES;
    forkJoin({
      job: this.cachedProjectService._getJobDetail(this.nzModalData.jobId).pipe(take(1)),
      project: this.cachedProjectService.getJobProject(this.nzModalData.jobId).pipe(take(1)),
      allProjects: this.cachedProjectService.getAllProjects().pipe(take(1)),
      allUsers: this.cachedUserService.userDetail.pipe(
        take(1),
        map((u) => u.all_users || []),
      ),
      
    })
      .pipe(
        finalize(() => (this.loading = false)), // Runs on success AND error
      )
      .subscribe({
        next: ({ job, project, allProjects, allUsers }) => {
          this.currentJob = job;
          this.allProjects = allProjects;
          this.allUsers = allUsers;
          this.allJobs = project.jobs
          this.project = project
          this.initializeForm();
        },
        error: (err) => {
          console.log("error occurred", err);
          this.notificationService.error(
            "There was an error while fetching data",
            err.error?.message || err.error?.detail || err.message || "An unexpected error occurred"
          );
        },
      });
  }

  initializeForm() {
    let parentJob = this.allJobs.find(j => j.id === this.currentJob.parent_job_id)
    this.jobForm.setValue({
      name: this.currentJob.name,
      projectId: this.currentJob.projectId,
      basePath: this.currentJob.basePath,
      iterations: this.currentJob.iterations,
      iterationsComplete: this.currentJob.iterationsComplete,
      comments: this.currentJob.comments,
      owner: this.currentJob.owner,
      parentJob: parentJob ?? null,
      parentCpNum: parentJob ? this.currentJob.parent_cp_num : null,
      isOffshoot: this.currentJob.offshoot ?? false,
      isAWS: this.currentJob.is_aws ?? false,
      jobType: this.currentJob.type ?? null,
      originalPath: this.currentJob.originalPath ?? null
    });
    // currentJob
    
    // add the validators and initializations for parentCpNum
    if (!parentJob){
      this.jobForm.get('parentCpNum').disable()
    }
    this.parentJobMaxIters = parentJob?.iterationsComplete ?? 1
    this.jobForm.get("parentCpNum").setValidators([
      Validators.required,
      Validators.pattern("^[0-9]+$"),
      (control: UntypedFormControl) => {
        if (control.value > this.parentJobMaxIters) {
          return { cpInvalid: true };
        }
        return null;
      },
    ]);

    // subscribe to the valueChanges observable after first initialization for parentJob to work with parentCp + parentJob changes
    this.jobForm.get('parentJob').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue) => {
      this.handleParentJobChange(newValue);
    });

    this.jobForm.get('projectId').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue) => {
      this.handleProjectChange(newValue);
    });
  }

  handleProjectChange(projectId: string){
    this.cachedProjectService.getProjectById(projectId).pipe(take(1)).subscribe(value => {
      this.allJobs = value.jobs
      this.jobForm.patchValue({
        parentJob: null,
        parentCpNum: null
      })
    })
  }

  handleParentJobChange(parentJob: Job) {
    if (!parentJob){
      this.jobForm.get('parentCpNum').patchValue(null)
      this.jobForm.get('parentCpNum').disable()
      this.jobForm.get('isOffshoot').patchValue(false)
      this.jobForm.get('isOffshoot').disable()
    }else{
      this.jobForm.get('parentCpNum').enable()
      this.jobForm.get('isOffshoot').enable()
      this.jobForm.get('parentCpNum').patchValue(0)
      this.parentJobMaxIters = parentJob?.iterationsComplete ?? 1
      this.jobForm.get("parentCpNum").setValidators([
        Validators.required,
        Validators.pattern("^[0-9]+$"),
        (control: UntypedFormControl) => {
          if (control.value > this.parentJobMaxIters) {
            return { cpInvalid: true };
          }
          return null;
        },
      ]);
    }

  }

  // used in template
  updateIterationsValidator(): void {
    Promise.resolve().then(() =>
      this.jobForm?.controls?.iterations.updateValueAndValidity(),
    );
    Promise.resolve().then(() =>
      this.jobForm?.controls?.iterationsComplete.updateValueAndValidity(),
    );
    this.jobForm.controls.iterations.markAsTouched();
    this.jobForm.controls.iterationsComplete.markAsTouched();
  }
  iterationsValidator = (control: UntypedFormControl) => {
    if (
      this.jobForm?.controls?.iterationsComplete.value >
      this.jobForm?.controls?.iterations.value
    ) {
      return { iterationsInvalid: true };
    }
    return {};
  };

  compareFn = (o1: any, o2: any): boolean =>
    o1 && o2 ? o1.id === o2.id : o1 === o2;

  ngOnDestroy(){
    this.destroy$.next()
    this.destroy$.complete()
  }

  onCancel = this.#modal.destroy
  onSave(){
    this.loading = true
    let values = this.jobForm.value
    let data = {
      id: this.currentJob.id,
      name: values.name,
      projectId: values.projectId,
      basePath: values.basePath,
      iterations: values.iterations,
      iterationsComplete: values.iterationsComplete,
      owner: values.owner.id,
      comments: values.comments,
      parent_job_id: values.parentJob?.id ?? null,
      parent_cp_num: values.parentJob ? (values.parentCpNum ?? 0) : null, // only send if parentJob has a value otherwise just send null
      offshoot: values.isOffshoot,
      is_aws: values.isAWS,
      type: values.jobType,
      originalPath: values.originalPath,
    }
    if (this.jobForm.valid){
      
      this.cachedProjectService
        .updateJobDetails(data)
        .pipe(take(1), finalize(() => this.loading = false))
        .subscribe({
          next: (data) => {
            this.cachedProjectService.clearProjectsCache();
            this.cachedProjectService.clearJobDetailCache([this.currentJob.id]);
            
            this.notificationService.success("Job Details updated", null);

            this.#modal.destroy(true)
            if (data.project !== this.project.id) {
              this.router.navigate(["/projects"]);
              return;
            }
          },
          error: (error) => {
            console.log('error', error)
            this.notificationService.error(
              "Job details could not be updated",
              error.error?.message || error.error?.detail || error.message || "An unexpected error occurred"
            );
          },}
        );
    }
  }
  
  onReparse(){
    // clear cache for job + project
    this.cachedProjectService.clearProjectsCache();
    this.cachedProjectService.clearJobDetailCache([this.currentJob.id]);

    // construct entries and reparse job
    let entries = [
      {
        path: `/${this.currentJob.basePath}`,
        parent_job_id: this.currentJob.parent_job_id,
        parent_cp_num: this.currentJob.parent_cp_num,
        x2_is_inline: this.currentJob.x2_is_inline,
        is_update: true,
        is_aws: this.currentJob.is_aws
      },
    ];
    this.loading = true;
    this.cachedProjectService
      .createJobs(this.currentJob.projectId, entries)
      .pipe(take(1), finalize(()=> this.loading = false))
      .subscribe({
        next: (res) => {
          // failed | error messages
          res["failed"].forEach((element) => {
            this.notificationService.error(
              "Error",
              `Job with path: <div>${element["path"]} </div> <br> failed to be added to the project: <br><b>${element["error"]}</b>`,
            );
          });

          // warning messages
          res["passed"].forEach((element) => {
            let warnings = element["warnings"] || [];
            warnings.forEach((warning: string) => {
              this.notificationService.warning("Warning", warning);
            });
            this.notificationService.success(
              "Success",
              `Job <div><b>${element["path"]}</b></div> was successfully re-parsed`,
            );
          });
          // passed, close modal and retrun true for reload of stuff
          if (res["passed"].length > 0) {
            this.#modal.destroy(true);
          }
        },
        error: (err) => {
          this.notificationService.error(
            "Error",
            err.error?.error || "An unexpected error occured",
          );
        },
      });
  }

  calculateCost(){
    this.loading = true;
    firstValueFrom(this.projectService.calculateJobCost(this.currentJob.id))
    .then(res => {
      const {data, type} = res
      // data is the numerical cost value, ad type is the cost type
      // getting the data is sort of irrelevant here tho, we sort of just need to fetch the job again
      // there is a possibility that we may not need to refresh the entire project cache etc. but the refractor for this will be in the future and not right now
      this.cachedProjectService.clearProjectsCache();
      this.cachedProjectService.clearJobDetailCache([this.currentJob.id]);
      this.notificationService.success(`Cost for job: ${this.currentJob.name} updated.`, null);
      this.#modal.destroy(true)
    }).catch((err) => {
      console.log('Error calculating job cost: ', err?.error?.error || err?.error || "There was an unexpected error")
      this.notificationService.error('Error calculating job cost', err?.error?.error || err?.error || "There was an unexpected error");
    }).finally(() => this.loading = false)
    
  }
}
